import { useState, useEffect, useRef, useCallback } from "react";

import { API_BASE_URL } from "../config/api";

const cache = {};

export function useFaqs({ category, pageSize = 50 } = {}) {
  const cacheKey = `${category ?? "all"}:${pageSize}`;
  const [faqs, setFaqs] = useState(cache[cacheKey] ?? []);
  const [loading, setLoading] = useState(!cache[cacheKey]);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const fetchIdRef = useRef(0);

  const fetchFaqs = useCallback(() => {
    if (!API_BASE_URL) {
      setFaqs([]);
      setLoading(false);
      setError("API is not configured.");
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const fetchId = ++fetchIdRef.current;

    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ pageSize: String(pageSize) });
    if (category) params.set("category", category);

    fetch(`${API_BASE_URL}/faqs?${params}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Unable to load FAQs (HTTP ${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (fetchId !== fetchIdRef.current) return;

        const items = json.faqs;
        if (Array.isArray(items) && items.length > 0) {
          cache[cacheKey] = items;
          setFaqs(items);
          setError(null);
        } else {
          setFaqs([]);
          setError("No FAQs are available right now.");
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError" || fetchId !== fetchIdRef.current) return;
        setFaqs([]);
        setError(err.message || "Unable to load FAQs.");
        setLoading(false);
      });
  }, [cacheKey, category, pageSize]);

  useEffect(() => {
    if (cache[cacheKey]) {
      setFaqs(cache[cacheKey]);
      setLoading(false);
      setError(null);
      return;
    }

    fetchFaqs();
    return () => abortRef.current?.abort();
  }, [cacheKey, fetchFaqs]);

  const refetch = useCallback(() => {
    delete cache[cacheKey];
    fetchFaqs();
  }, [cacheKey, fetchFaqs]);

  return { faqs, loading, error, refetch };
}
